'use client';
import { PropsWithChildren, useState, createContext, useContext } from 'react';
import { QueryObserverResult, useQuery } from 'react-query';
import {
  QueueDataProps,
  QuizQueueQueryResponse,
  QuizSpacedRepetition,
} from '../types/queues';
import { useFetch } from './FetchProvider';
import {
  fetchQuizQueues,
  QUEUE_REFETCH_INTERVAL,
} from '../lib/frontend/quiz/queues';
import { useUserContext } from './UserContext';

const NOTIFICATION_TIMEOUT = 5000;

export type QueueItemType = 'flashcards' | 'questions';

export type SaveToQueueFunc = (
  nodeIDs: number[],
  type: QueueItemType,
  silent?: boolean,
) => Promise<{ success: number }>;

type QueueContextProps = {
  queues: QueueDataProps;
  saveToQueue: SaveToQueueFunc;
  fetchQueues: () =>
    | Promise<QueryObserverResult<QuizQueueQueryResponse, unknown>>
    | Promise<void>;
  isLoading: boolean;
  savedIds: { [key in QueueItemType]: number[] };
};

const parseSpacedRepetitionQuiz = (quiz?: QuizSpacedRepetition) =>
  quiz
    ? quiz.ids.map((id, i) => ({
        age: quiz.age[i],
        nodeID: id,
      }))
    : [];

const QueueContext = createContext<QueueContextProps>({
  queues: {},
  saveToQueue: async () => {
    return { success: 0 };
  },
  registerOnSave: () => {
    /* nothin' */
  },
  fetchQueues: async () => {
    /* nothin' */
  },
  isLoading: false,
  savedIds: {
    flashcards: [],
    questions: [],
    item: [],
  },
} as QueueContextProps);

type Props = Record<string, unknown>;

/**
 * QueueProvider.
 */
const QueueProvider = (props: PropsWithChildren<Props>): JSX.Element => {
  const { children } = props;

  const { get, post } = useFetch();
  const { user } = useUserContext();

  const [savedIds, setSavedIds] = useState({
    flashcards: [],
    questions: [],
    item: [],
  });

  const {
    data = {},
    refetch,
    isLoading,
  } = useQuery('quizQueues', async () => fetchQuizQueues(get), {
    refetchInterval: QUEUE_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    // refetchOnMount: true,
    // refetchOnWindowFocus: 'always',
    enabled: Boolean(user?.userID),
  });

  const flashcards = data?.flashcards;
  const questions = data?.questions;
  const spacedRepetition = data?.spacedRepetition && {
    osmosis: {
      ...data.spacedRepetition.osmosis,
      items: parseSpacedRepetitionQuiz(data.spacedRepetition.osmosis?.quiz),
      completedToday: data.spacedRepetition.osmosis?.completedToday || [],
      correct: data.spacedRepetition.osmosis?.correct || 0,
      quiz: data.spacedRepetition.osmosis?.quiz ?? {
        ids: [],
        age: [],
      },
    },
    local: {
      ...data.spacedRepetition.local,
      items: parseSpacedRepetitionQuiz(data.spacedRepetition.local?.quiz),
      completedToday: data.spacedRepetition.local?.completedToday || [],
      correct: data.spacedRepetition.local?.correct || 0,
      quiz: data.spacedRepetition.local?.quiz ?? {
        ids: [],
        age: [],
      },
    },
  };

  const queues: QueueDataProps = {
    flashcards,
    questions,
    spacedRepetition,
  };

  const handleSaveToQueue: SaveToQueueFunc = async (
    nodeIDs,
    type: QueueItemType,
    silent = false,
  ) => {
    const { data } = (await post('/saveToQueue', {
      items: nodeIDs,
      type,
    })) as { data: { success: number } };

    if (silent) {
      return data;
    }

    if (data.success) {
      await refetch();
      setSavedIds((prev) => ({ ...prev, [type]: nodeIDs }));
    }
    setTimeout(() => {
      setSavedIds((prev) => ({ ...prev, [type]: [] }));
    }, NOTIFICATION_TIMEOUT);

    return data;
  };

  return (
    <QueueContext.Provider
      value={{
        isLoading,
        queues,
        fetchQueues: refetch,
        saveToQueue: handleSaveToQueue,
        savedIds: savedIds,
      }}
    >
      {children}
    </QueueContext.Provider>
  );
};

/**
 * UseQueueContext.
 */
export const useQueueContext = () => useContext(QueueContext);

export default QueueProvider;
